import { FiscalEcoleReleased, FiscalKeys, FiscalVerifyReport, IFiscalAccountingExportSummary, IFiscalArchiveSummary, IFiscalRegisterSummary } from '@tickeat/types';
import { IOrder } from '@app/common';
import { rootApi } from '../api';

// Extrait de la définition d'endpoint pour rester testable : `api.endpoints.X`
// n'expose que `initiate`, `select` et les matchers — jamais `onQueryStarted`.
export const suivreArchiveTelechargee = async (
  _archiveId: string,
  { queryFulfilled }: { queryFulfilled: Promise<{ data: { url: string } }> },
) => {
  // RTK jette la promesse rendue par `onQueryStarted` : sans ce `try`, une
  // archive introuvable ou une session expirée produit un rejet non capturé.
  // L'appelant reçoit de toute façon l'erreur via `unwrap()`.
  try {
    const { data } = await queryFulfilled;

    // Pas de `window.open` : après un `await`, Safari ne rattache plus
    // l'ouverture au clic et bloque la fenêtre. L'URL présignée renvoie
    // l'archive en pièce jointe, la navigation télécharge sans quitter la page.
    window.location.assign(data.url);
  } catch {
    // L'appelant gère l'erreur via `unwrap()`.
  }
};

// Export de période : la réponse est un FICHIER, pas du JSON parsé — d'où le
// `responseHandler`. Extrait comme `suivreArchiveTelechargee`, pour la même
// raison : `api.endpoints.X` n'expose jamais les options de `query`.
//
// LE CORPS D'ERREUR N'EST PAS UN EXPORT (revue finale 9c-B, I1).
// `fetchBaseQuery` appelle `responseHandler` AVANT `validateStatus` : sans ce
// premier test, un 403 (« caisse d'une autre boutique »), un 422 (« bornes
// invalides ») ou un 500 se retrouvait téléchargé sous le nom
// `export-CAISSE-….json`. L'administrateur ouvrait un export qui contenait un
// message d'erreur, et aucun toast ne l'avertissait. Rendre le corps parsé le
// remet sur le chemin normal : `validateStatus` le refuse, `unwrap()` le jette,
// la page l'affiche.
export const suivreExportTelecharge = (
  { registerCode, from, to }: { registerCode: string; from?: string; to?: string },
) => async (response: Response) => {
  if (!response.ok) return response.json();

  const texte = await response.text();
  const blob = new Blob([texte], { type: 'application/json;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `export-${registerCode}-${from ?? 'debut'}-${to ?? 'fin'}.json`;
  document.body.appendChild(a);
  a.click();
  // Ancre retirée tout de suite ; seul le relâchement de l'URL attend un tour,
  // sans toucher au document (même règle que `telechargerBlob`, service.ts).
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

// I3 (fix round 1, E-22) : extraites pour être testées sans construire un store RTK Query
// complet — le contrat qui compte est la FORME du tag invalidé, pas le mécanisme
// d'invalidation lui-même (déjà éprouvé par RTK Query). Sans elles, « Dernière vérification »
// ne bougeait qu'au prochain remontage de la fiche ou de la liste, jamais au clic qui vient
// de l'écrire côté engine.
export const tagsApresVerificationRegistre = (registerId: string) => [{ type: 'FiscalRegisters' as const, id: registerId }];
// Préfixe `ARCHIVE-` (fix round 2, nit) : même convention que `SHOP-…` sur
// `readFiscalRegisters` ci-dessous — distingue l'étiquette par archive de celle par registre
// dans le même espace de noms `FiscalArchives`.
export const tagsApresVerificationArchive = (archiveId: string) => [{ type: 'FiscalArchives' as const, id: `ARCHIVE-${archiveId}` }];

// Page « Conformité NF525 » (chantier 9c-B). Toutes les routes vivent sur la
// gateway dashboard sous `/fiscal` et portent la boutique par `x-shop`.
export const fiscalApi = rootApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    // `shopId` facultatif : sans lui, la gateway répond pour la boutique
    // sélectionnée (en-tête `x-shop` posé par `prepareHeaders`), ce que veulent
    // les écrans « Conformité ». La fiche boutique, elle, peut être ouverte sur
    // une AUTRE boutique de l'organisation : elle nomme la sienne, et
    // `prepareHeaders` respecte l'en-tête déjà posé.
    readFiscalRegisters: builder.query<IFiscalRegisterSummary[], { shopId?: string } | void>({
      query: (arg) => ({
        url: 'fiscal/registers',
        method: 'GET',
        headers: arg?.shopId ? { 'x-shop': arg.shopId } : undefined,
      }),
      // `LIST` est fourni par TOUTES les variantes, y compris celles portant un
      // `shopId` : `declareFiscalRegisterLost` n'invalide que `LIST`, et une
      // liste qui ne le fournirait pas resterait périmée après une déclaration.
      // L'étiquette par boutique est préfixée pour ne pas entrer en collision
      // avec les identifiants de caisse du même espace de noms.
      providesTags: (result, _error, arg) => [
        { type: 'FiscalRegisters', id: 'LIST' },
        ...(arg?.shopId ? [{ type: 'FiscalRegisters' as const, id: `SHOP-${arg.shopId}` }] : []),
        ...(result ?? []).map((r) => ({ type: 'FiscalRegisters' as const, id: r.registerId })),
      ],
    }),
    readFiscalArchives: builder.query<IFiscalArchiveSummary[], string>({
      query: (registerId) => ({ url: `fiscal/registers/${registerId}/archives`, method: 'GET' }),
      // I3 (fix round 1, E-22) : chaque archive est AUSSI étiquetée par son propre id
      // (préfixe `ARCHIVE-`, fix round 2 nit — même convention que `SHOP-…` ci-dessus) —
      // `verifyFiscalArchive` ne connaît que l'archive vérifiée (jamais son `registerId`,
      // l'appelant existant ne le porte pas), c'est le seul lien disponible pour invalider
      // cette liste après une vérification.
      providesTags: (result, _error, registerId) => [
        { type: 'FiscalArchives', id: registerId },
        ...(result ?? []).map((a) => ({ type: 'FiscalArchives' as const, id: `ARCHIVE-${a._id}` })),
      ],
    }),
    readFiscalKeys: builder.query<FiscalKeys, void>({
      query: () => ({ url: 'fiscal/keys', method: 'GET' }),
    }),
    // Piste d'audit des exports comptables (E-07 volet 2, 6.14.2.1/6.14.2.2, chantier lot 6
    // tâche 14) : append-only côté `cash` (tâche 13) — un export réussi ou refusé écrit
    // toujours une ligne (tâche 12/13), jamais visible sans recharger cette liste. Invalidée
    // par `generateCsvSummary` (`services/service.ts`) quand il ABOUTIT (RTK Query
    // n'invalide `invalidatesTags` que sur une mutation `fulfilled`) ; un REFUS 422 structuré
    // écrit aussi une ligne (best-effort, côté engine) mais ne déclenche aucun
    // `invalidatesTags` — `ServiceList.tsx` refetch explicitement cette liste après un refus.
    readFiscalAccountingExports: builder.query<IFiscalAccountingExportSummary[], void>({
      query: () => ({ url: 'fiscal/accounting-exports', method: 'GET' }),
      providesTags: [{ type: 'FiscalAccountingExports', id: 'LIST' }],
    }),
    // I3 (fix round 1, E-22) : sans `invalidatesTags`, « Dernière vérification » ne bougeait
    // qu'au prochain remontage de la fiche ou de la liste — jamais au clic qui vient de
    // l'écrire côté engine.
    verifyFiscalRegister: builder.mutation<FiscalVerifyReport, string>({
      query: (registerId) => ({ url: `fiscal/registers/${registerId}/verify`, method: 'POST' }),
      invalidatesTags: (_r, _e, registerId) => tagsApresVerificationRegistre(registerId),
    }),
    verifyFiscalArchive: builder.mutation<FiscalVerifyReport, string>({
      query: (archiveId) => ({ url: `fiscal/archives/${archiveId}/verify`, method: 'POST' }),
      invalidatesTags: (_r, _e, archiveId) => tagsApresVerificationArchive(archiveId),
    }),
    // L'URL S3 présignée expire en quinze minutes : jamais mise en cache, suivie
    // aussitôt pour déclencher le téléchargement.
    downloadFiscalArchive: builder.mutation<{ url: string; expiresAt: string }, string>({
      query: (archiveId) => ({ url: `fiscal/archives/${archiveId}/download`, method: 'GET', cache: 'no-cache' }),
      onQueryStarted: suivreArchiveTelechargee,
    }),
    exportFiscalRegister: builder.mutation<void, { registerId: string; registerCode: string; from?: string; to?: string }>({
      query: ({ registerId, registerCode, from, to }) => ({
        url: `fiscal/registers/${registerId}/export`,
        method: 'GET',
        params: { ...(from ? { from } : {}), ...(to ? { to } : {}) },
        cache: 'no-cache',
        responseHandler: suivreExportTelecharge({ registerCode, from, to }),
      }),
    }),
    retryFiscalArchive: builder.mutation<{ requested: true; archiveId: string }, { archiveId: string; registerId: string }>({
      query: ({ archiveId }) => ({ url: `fiscal/archives/${archiveId}/retry`, method: 'POST' }),
      invalidatesTags: (_r, _e, { registerId }) => [{ type: 'FiscalArchives', id: registerId }],
    }),
    declareFiscalRegisterLost: builder.mutation<{ declared: true; registerId: string; already: boolean }, string>({
      query: (registerId) => ({ url: `fiscal/registers/${registerId}/declare-lost`, method: 'POST' }),
      invalidatesTags: (_r, _e, registerId) => [{ type: 'FiscalRegisters', id: 'LIST' }, { type: 'FiscalArchives', id: registerId }],
    }),
    // La SEULE lecture du dashboard qui voit les commandes d'entraînement :
    // partout ailleurs l'engine les exclut. Sans elle, le relâchement ci-dessous
    // n'est atteignable que par l'API.
    readFiscalEcoleOrders: builder.query<IOrder[], void>({
      query: () => ({ url: 'fiscal/orders/ecole', method: 'GET' }),
      providesTags: [{ type: 'FiscalEcoleOrders', id: 'LIST' }],
    }),
    releaseFiscalEcole: builder.mutation<FiscalEcoleReleased, { appId: string; reason: string }>({
      query: ({ appId, reason }) => ({ url: `fiscal/orders/${appId}/ecole/release`, method: 'POST', body: { reason } }),
      // La commande relâchée quitte la liste d'entraînement et entre dans le
      // service : les deux caches sont périmés par le geste.
      invalidatesTags: [{ type: 'Services', id: 'LIST' }, { type: 'FiscalEcoleOrders', id: 'LIST' }],
    }),
    // 6.9.4.2 (Task 8, E-37) : la documentation de l'archive doit être
    // consultable À TOUT INSTANT, y compris avant qu'une seule archive
    // n'existe. Aucun paramètre : la route ne dépend d'aucun tenant, d'aucune
    // boutique, d'aucune caisse — une constante du build, servie telle quelle.
    // Chargée eagerly (pas de `useLazy…`) : `ouvrirLaDocumentation`
    // (`FiscalArchives.tsx`) ouvre le blob de façon SYNCHRONE dans le
    // gestionnaire de clic, à partir du contenu déjà en cache — jamais après
    // un `await`, sous peine du même blocage Safari que documente
    // `suivreArchiveTelechargee` ci-dessus.
    lireDocumentationArchive: builder.query<{ contentType: string; content: string }, void>({
      query: () => ({ url: 'fiscal/documentation', method: 'GET' }),
    }),
  }),
});
